import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type {
  IAssistantScript,
  TriggerType,
} from '@mytaverse-shared/interfaces/assistantScript';
import type { PollyLanguage } from '@mytaverse-shared/interfaces/polly';
import ChoiceCard from '../ChoiceCard';
import ButtonLanguageEditor from '../ButtonLanguageEditor';
import {
  TriggerAreaCard,
  TriggerAreaCardLabel,
  TriggerAreaCardObjectIcon,
  TriggerAreaCardPinIcon,
  TriggerAreaCardTitle,
  TriggerInfoBox,
  TriggerTogglePinIcon,
  TriggerToggleTouchIcon,
  UiClickSectionLabel,
} from './styled';

interface TriggerSectionProps {
  triggerType: TriggerType;
  title?: string;
  assistantScript?: IAssistantScript | null;
  mapName?: string;
  activeLanguage: PollyLanguage;
  effectiveBaseLanguage: PollyLanguage;
  nonBaseLangs: PollyLanguage[];
  uiClickButtonLabel: string;
  uiClickButtonLabelTranslations: Partial<Record<PollyLanguage, string>>;
  uiClickButtonLabelStale: Partial<Record<PollyLanguage, boolean>>;
  isTranslating: boolean;
  onTriggerTypeChange: (type: TriggerType) => void;
  onActiveLanguageChange: (lang: PollyLanguage) => void;
  onUiClickButtonLabelChange: (label: string) => void;
  onUiClickButtonLabelTranslationChange: (
    lang: PollyLanguage,
    label: string,
  ) => void;
  onTranslateUiClickButtonLabel: (langs: PollyLanguage[]) => void;
}

const TriggerSection = ({
  triggerType,
  title,
  assistantScript,
  mapName,
  activeLanguage,
  effectiveBaseLanguage,
  nonBaseLangs,
  uiClickButtonLabel,
  uiClickButtonLabelTranslations,
  uiClickButtonLabelStale,
  isTranslating,
  onTriggerTypeChange,
  onActiveLanguageChange,
  onUiClickButtonLabelChange,
  onUiClickButtonLabelTranslationChange,
  onTranslateUiClickButtonLabel,
}: TriggerSectionProps) => {
  const { t } = useTranslation('common');
  const displayTitle = title ?? assistantScript?.title;

  return (
    <Box>
      <TriggerAreaCard>
        {triggerType === 'object' ? (
          <TriggerAreaCardObjectIcon />
        ) : (
          <TriggerAreaCardPinIcon />
        )}
        <Box>
          <TriggerAreaCardLabel>
            {triggerType === 'object'
              ? t('assistant.scriptDialog.triggerObject')
              : triggerType === 'custom_spot'
              ? t('assistant.scriptDialog.triggerCustomSpot')
              : t('assistant.scriptDialog.triggerArea')}
          </TriggerAreaCardLabel>
          <TriggerAreaCardTitle>{displayTitle}</TriggerAreaCardTitle>
          {mapName && (
            <TriggerAreaCardLabel sx={{ display: 'none' }}>
              {mapName}
            </TriggerAreaCardLabel>
          )}
        </Box>
      </TriggerAreaCard>

      {triggerType !== 'object' && (
        <>
          {triggerType !== 'custom_spot' && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.25,
                mb: 2,
              }}
            >
              <ChoiceCard
                icon={<TriggerTogglePinIcon />}
                title={t(
                  'assistant.scriptDialog.wizard.triggerAreaChoiceTitle',
                )}
                desc={t('assistant.scriptDialog.wizard.triggerAreaChoiceDesc')}
                selected={triggerType === 'area_entry'}
                onClick={() => onTriggerTypeChange('area_entry')}
                dataTestId="assistant-script-trigger-option-area_entry"
              />
              <ChoiceCard
                icon={<TriggerToggleTouchIcon />}
                title={t(
                  'assistant.scriptDialog.wizard.triggerUiClickChoiceTitle',
                )}
                desc={t(
                  'assistant.scriptDialog.wizard.triggerUiClickChoiceDesc',
                )}
                selected={triggerType === 'ui_click'}
                onClick={() => onTriggerTypeChange('ui_click')}
                dataTestId="assistant-script-trigger-option-ui_click"
              />
            </Box>
          )}

          {triggerType === 'area_entry' ? (
            <TriggerInfoBox>
              <Typography
                variant="body2"
                sx={{ fontSize: 12, color: 'var(--am-text-secondary)' }}
              >
                {t('assistant.scriptDialog.triggerAreaEntryDescPre')}{' '}
                <span
                  style={{ fontWeight: 700, color: 'var(--am-text-primary)' }}
                >
                  {displayTitle}
                </span>
                .
              </Typography>
            </TriggerInfoBox>
          ) : (
            <TriggerInfoBox>
              <UiClickSectionLabel>
                {t('assistant.scriptDialog.buttonInNoaDialog')}
              </UiClickSectionLabel>
              <Typography
                variant="body2"
                sx={{
                  fontSize: 12,
                  color: 'var(--am-text-secondary)',
                  mb: 1.5,
                }}
              >
                {t('assistant.scriptDialog.buttonInNoaDialogDescPre')}{' '}
                <span
                  style={{ fontWeight: 700, color: 'var(--am-text-primary)' }}
                >
                  {displayTitle}
                </span>
                . {t('assistant.scriptDialog.buttonInNoaDialogDescPost')}
              </Typography>
              <ButtonLanguageEditor
                effectiveBaseLanguage={effectiveBaseLanguage}
                nonBaseLangs={nonBaseLangs}
                activeLanguage={activeLanguage}
                uiClickButtonLabel={uiClickButtonLabel}
                uiClickButtonLabelTranslations={uiClickButtonLabelTranslations}
                uiClickButtonLabelStale={uiClickButtonLabelStale}
                isTranslating={isTranslating}
                onActiveLanguageChange={onActiveLanguageChange}
                onBaseLabelChange={onUiClickButtonLabelChange}
                onTranslationChange={onUiClickButtonLabelTranslationChange}
                onTranslateLangs={onTranslateUiClickButtonLabel}
              />
            </TriggerInfoBox>
          )}
        </>
      )}
    </Box>
  );
};

export default TriggerSection;
